/**
 * Resolves the `[n]` markers in an answer to the sources that back them.
 *
 * Callers must pass the accumulated text, never a single chunk: the model emits `[3]` as three
 * separate tokens, so a per-chunk parser never sees the pattern. Passing the whole buffer also
 * makes the live stream and a reopened conversation share one function and one result.
 */

const MARKER_PATTERN = /\[(\d{1,3})\]/g

/** Same closed partition as Bradley `type_intent.DKO_TYPE_TO_BUCKET`. */
export const DKO_TYPE_TO_BUCKET = {
  'Resume': 'People',
  'Standards and Procedures': 'Methodology',
  'Training Material': 'Methodology',
  'Template': 'Methodology',
  'Tool': 'Methodology',
  'Practice Report': 'Methodology',
  'Example Client Deliverable': 'Example',
  'Proposal': 'Commercial',
  'Sales Presentation': 'Commercial',
  'Project Credential': 'Commercial',
  'Project Credentials Pack': 'Commercial',
  'Company Profile': 'Commercial',
  'Companies List': 'Commercial',
  'Industry Profile': 'Commercial',
  'Macroeconomic Overview': 'Commercial',
  'Conference/External Presentation': 'Commercial',
}

export function dkoBucketLabel(rawType) {
  return DKO_TYPE_TO_BUCKET[(rawType || '').trim()] || ''
}

export function citationTooltip(citation, labels = {}) {
  const nameLabel = labels.name ?? 'Name'
  const typeLabel = labels.type ?? 'Type'
  const name = citation?.doc_title ?? ''
  const bucket = dkoBucketLabel(citation?.dko_document_type)
  if (bucket) {
    return `${nameLabel}: ${name}\n${typeLabel}: ${bucket}`
  }
  return name ? `${nameLabel}: ${name}` : ''
}

export function buildCitationMap(citations) {
  const map = new Map()
  for (const citation of citations ?? []) {
    const marker = Number(citation?.marker)
    if (Number.isInteger(marker) && marker > 0) {
      map.set(marker, citation)
    }
  }
  return map
}

/** Rebuilds the map from the references persisted with a message, for a reopened conversation. */
export function citationMapFromDocuments(documents) {
  const citations = []
  for (const document of documents ?? []) {
    const metadata = document?.metadata ?? {}
    const markers = document?.markers ?? metadata?.markers ?? []
    for (const marker of markers) {
      const citation = {
        marker,
        doc_title: document?.doc_title ?? metadata?.doc_title ?? '',
        share_point_url: document?.share_point_url ?? metadata?.share_point_url ?? '',
        source_index: document?.source_index ?? metadata?.source_index ?? '',
      }
      const documentType = (
        document?.dko_document_type
        ?? metadata?.dko_document_type
        ?? document?.document_type
        ?? metadata?.document_type
        ?? ''
      ).trim()
      if (documentType) {
        citation.dko_document_type = documentType
      }
      citations.push(citation)
    }
  }
  return buildCitationMap(citations)
}

/**
 * Splits the answer into text runs and the markers that resolve to a source.
 *
 * A marker with no source stays inside the text: the model invents `[4]` when three sources
 * came back, and guessing the closest document would produce a citation that looks right and
 * points at the wrong source.
 */
export function splitTextWithCitations(text, citationMap) {
  const source = typeof text === 'string' ? text : ''
  if (!source) {
    return []
  }

  const map = citationMap ?? new Map()
  const parts = []
  let lastIndex = 0

  for (const match of source.matchAll(MARKER_PATTERN)) {
    const citation = map.get(Number(match[1]))
    if (!citation) {
      continue
    }

    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: source.slice(lastIndex, match.index) })
    }
    parts.push({ type: 'citation', marker: Number(match[1]), citation })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < source.length) {
    parts.push({ type: 'text', value: source.slice(lastIndex) })
  }

  return parts
}

export function hasResolvableCitation(text, citationMap) {
  return splitTextWithCitations(text, citationMap).some(part => part.type === 'citation')
}
